import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  StateBackgroundCheck,
  UpdateStateBackgroundCheckRequest
} from '../../models/state-background-check.model';

/**
 * Reads and writes a contractor's manually verified state background check.
 *
 * Shared across the business, facility and contractor apps — the API scopes the
 * response to the caller's role, so all three consume the same endpoint and the
 * same model.
 */
@Injectable({ providedIn: 'root' })
export class StateBackgroundCheckService {
  private readonly apiUrl = '/api/contractors';
  private readonly http = inject(HttpClient);

  /**
   * Business and facility read. Returns 404 when no admin has created a record
   * yet, which is the normal starting state for most contractors — callers
   * should treat it as "not verified", not as an error.
   */
  get(contractorId: string): Observable<StateBackgroundCheck> {
    return this.http.get<StateBackgroundCheck>(
      `${this.apiUrl}/${contractorId}/state-background-check`
    );
  }

  /**
   * The signed-in contractor's own record, which omits the admin's notes. Uses
   * the dedicated `me` route rather than the id route, which a contractor is not
   * authorized to call. 404 when no record exists, as above.
   */
  getMine(): Observable<StateBackgroundCheck> {
    return this.http.get<StateBackgroundCheck>(
      `${this.apiUrl}/me/state-background-check`
    );
  }

  /** Business administrators only; other roles receive 403. */
  update(
    contractorId: string,
    payload: UpdateStateBackgroundCheckRequest
  ): Observable<StateBackgroundCheck> {
    return this.http.put<StateBackgroundCheck>(
      `${this.apiUrl}/${contractorId}/state-background-check`,
      payload
    );
  }
}
